import React from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import { Text } from 'apps/auth/components/common/Text/Text';
import { INCREASE_LIMITS_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { GA_LEVEL } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { resolveArguments, toLowerCase } from 'apps/auth/helpers/utils';
import { useExtra } from 'apps/auth/hooks/selectors';

import { LimitBlock } from './LimitBlock';

const GaLimits = (props) => {
    const { gaCurrentLimitLevel, profile_form } = useExtra();
    const expectedGALevel = gaCurrentLimitLevel + 1;
    const isThreeMonthLimits = profile_form?.threeMonthLimitEnabled;

    const isCurrLessMaxValue = (counter) => {
        return counter.current_value < counter.max_value;
    };

    const gaLimitProcessButton = () => {
        return (
            <Panel
                type="link"
                href={props.aml_ga_increase_level_link}
                onClick={() => amplitude.getInstance().logEvent(INCREASE_LIMITS_SHOWN, { [GA_LEVEL]: expectedGALevel })}
                isHoverable
                negativeMargin
            >
                <DiskBadgeWrapper>
                    <DiskBadge variant="secondary" iconName="arrow-up" />
                </DiskBadgeWrapper>
                <Text color="primary">{props.gaLimitProcessButton}</Text>
            </Panel>
        );
    };

    return (
        <>
            {props.ga_from_account_limit && (
                <>
                    <Text variant="div" indent="8px">
                        {props.gaLimitInfo}
                    </Text>
                    {props.ga_from_account_limit.locked && (
                        <Text variant="div" size="header" color="secondary">
                            {props.gaLimitLocked}
                        </Text>
                    )}
                    {props.ga_from_account_limit.unlimited ||
                        (props.ga_from_account_limit.active && (
                            <Text variant="div" size="header" color="primary">
                                {props.gaLimitUnlim}
                            </Text>
                        ))}
                    {props.ga_from_account_limit.locked && !isThreeMonthLimits && gaLimitProcessButton()}
                </>
            )}
            {props.topup_limit_6_months && props.topup_limit_6_months_counter && props.topup_limit_6_months_counter.topUpLimitOneOff && (
                <>
                    <Separator marginBottom="48px" />
                    <HeaderPanel title={props.gaLimitTopUpInfo} indentCompact />
                    <Row indent="adaptive-32-48">
                        <Text variant="div" size="header" color={isCurrLessMaxValue(props.topup_limit_6_months_counter) ? 'primary' : 'error'} indent="4px">
                            {props.gaLimitTopUpOneOff}
                        </Text>
                        <Text variant="div" size="secondary" color={isCurrLessMaxValue(props.topup_limit_6_months_counter) ? 'primary' : 'error'}>
                            {resolveArguments(props.gaLimitTopUpTo, props.topup_limit_6_months.max_value) + ' '}
                            <span style={{ display: 'none' }}>
                                props.gaLimitTopUpTo: {props.gaLimitTopUpTo},<br />
                                props.topup_limit_6_months.max_value: {props.topup_limit_6_months.max_value}
                            </span>
                            <Text size="secondary" color={isCurrLessMaxValue(props.topup_limit_6_months_counter) ? 'primary' : 'error'} smallCaps>
                                {toLowerCase(props.topup_limit_6_months.cur_iso)}
                            </Text>
                        </Text>
                    </Row>
                    {!isThreeMonthLimits && gaLimitProcessButton()}
                </>
            )}
            {props.topup_limit_6_months && props.topup_limit_6_months_counter && !props.topup_limit_6_months_counter.topUpLimitOneOff && (
                <>
                    <Separator marginBottom="48px" />
                    <HeaderPanel title={props.gaLimitTopUpInfo} indentCompact />
                    {props.topup_limit_6_months.unlimited && (
                        <Text variant="div" size="header" color="primary">
                            {props.gaLimitUnlim}
                        </Text>
                    )}
                    {props.topup_limit_6_months.active && (
                        <>
                            <Row indent="adaptive-32-48">
                                <LimitBlock
                                    progressWidth={props.topup_limit_6_months.usedPercent}
                                    isExhaustedLimit={props.alarm_limits <= props.topup_limit_6_months.usedPercent}
                                    currency={toLowerCase(props.topup_limit_6_months.cur_iso)}
                                    spendSum={getLocalizedRawAmount(props.lang, props.country, props.topup_limit_6_months.current_value)}
                                    spendText={props.profileSpent}
                                    leftSum={getLocalizedRawAmount(props.lang, props.country, props.topup_limit_6_months.leftValue)}
                                    leftText={resolveArguments(props.gaResetLimitInfo, [
                                        props.topup_limit_6_months.cycleResetDateDayOfMonth,
                                        props.gaTopUp6ResetMonth,
                                        props.topup_limit_6_months.cycleResetDateYear,
                                    ])}
                                />
                            </Row>
                            {!isThreeMonthLimits && gaLimitProcessButton()}
                        </>
                    )}
                </>
            )}
            {(props.ga_atm_1_month_limit || props.ga_atm_6_months_limit) && (
                <>
                    <Separator marginBottom="48px" />
                    <HeaderPanel title={props.gaAtmLimitInfo} indentCompact />
                </>
            )}
            {props.ga_atm_6_months_limit && (
                <>
                    {props.ga_atm_6_months_limit.unlimited && (
                        <Text variant="div" size="header" color="primary">
                            {props.gaLimitUnlim}
                        </Text>
                    )}
                    {props.ga_atm_6_months_limit.active && (
                        <Row indent="adaptive-32-48">
                            <LimitBlock
                                progressWidth={props.ga_atm_6_months_limit.usedPercent}
                                isExhaustedLimit={props.alarm_limits <= props.ga_atm_6_months_limit.usedPercent}
                                currency={toLowerCase(props.ga_atm_6_months_limit.cur_iso)}
                                spendSum={getLocalizedRawAmount(props.lang, props.country, props.ga_atm_6_months_limit.current_value)}
                                spendText={props.profileSpent}
                                leftSum={getLocalizedRawAmount(props.lang, props.country, props.ga_atm_6_months_limit.leftValue)}
                                leftText={
                                    <>
                                        {resolveArguments(props.gaResetLimitInfo, [
                                            props.ga_atm_6_months_limit.cycleResetDateDayOfMonth,
                                            props.gaAtm6ResetMonth,
                                            props.topup_limit_6_months.cycleResetDateYear,
                                        ])}
                                    </>
                                }
                            />
                        </Row>
                    )}
                </>
            )}
            {props.ga_atm_1_month_limit && (
                <Row indent="48px">
                    <LimitBlock
                        progressWidth={props.ga_atm_1_month_limit.usedPercent}
                        isExhaustedLimit={props.alarm_limits <= props.ga_atm_1_month_limit.usedPercent}
                        currency={toLowerCase(props.ga_atm_1_month_limit.cur_iso)}
                        spendSum={getLocalizedRawAmount(props.lang, props.country, props.ga_atm_1_month_limit.current_value)}
                        spendText={props.profileSpent}
                        leftSum={getLocalizedRawAmount(props.lang, props.country, props.ga_atm_1_month_limit.leftValue)}
                        leftText={
                            <>
                                {resolveArguments(props.gaResetLimitInfo, [
                                    props.ga_atm_1_month_limit.cycleResetDateDayOfMonth,
                                    props.gaAtm1ResetMonth,
                                    props.topup_limit_6_months.cycleResetDateYear,
                                ])}
                            </>
                        }
                    />
                </Row>
            )}
            {props.isSuccessKycConfirmation && (
                <>
                    <Separator />
                    <Text variant="div" size="secondary" color="secondary">
                        {props.kycNotification}
                    </Text>
                </>
            )}
        </>
    );
};

const mapStateToProps = (state) => ({
    alarm_limits: state.extra.alarmLimits,
    aml_ga_increase_level_link: state.extra.aml_ga_increase_level_link,
    ga_from_account_limit: state.extra.ga_from_account_limit,
    topup_limit_6_months: state.extra.topup_limit_6_months,
    topup_limit_6_months_counter: state.extra.topup_limit_6_months_counter,
    ga_atm_1_month_limit: state.extra.ga_atm_1_month_limit,
    ga_atm_6_months_limit: state.extra.ga_atm_6_months_limit,
    gaLimitInfo: state.messages.pageMessages.gaLimitInfo,
    gaLimitLocked: state.messages.pageMessages.gaLimitLocked,
    gaLimitUnlim: state.messages.pageMessages.gaLimitUnlim,
    gaLimitProcessButton: state.messages.pageMessages.gaLimitProcessButton,
    gaLimitTopUpInfo: state.messages.pageMessages.gaLimitTopUpInfo,
    gaLimitTopUpOneOff: state.messages.pageMessages.gaLimitTopUpOneOff,
    gaLimitTopUpTo: state.messages.pageMessages.gaLimitTopUpTo,
    gaResetLimitInfo: state.messages.pageMessages.gaResetLimitInfo,
    gaTopUp6ResetMonth: state.messages.pageMessages.gaTopUp6ResetMonth,
    gaAtm6ResetMonth: state.messages.pageMessages.gaAtm6ResetMonth,
    gaAtm1ResetMonth: state.messages.pageMessages.gaAtm1ResetMonth,
    gaAtmLimitInfo: state.messages.pageMessages.gaAtmLimitInfo,
    profileSpent: state.messages.pageMessages.profileSpent,
    isSuccessKycConfirmation: state.extra.isSuccessKycConfirmation,
    kycNotification: state.messages.pageMessages.kycNotification,
});
export default connect(mapStateToProps)(GaLimits);
